import type { GridFilterModel, GridSortModel } from '@repo/go-table';
import type { AxiosInstance } from 'axios';

import type { TAutoAnomaliesFilterKeys } from './AUTO_ANOMALIES_FIELD_CONFIG';
import { getApiField, isApplicableFilterItem } from './filter-utils';
import type { TAutoAnomalyRowDto } from './types';

export type TPageResponse<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

type TAutoAnomaliesRequestParams = {
  wellId: number;
  page?: number;
  size?: number;
  filter?: GridFilterModel;
  sort?: GridSortModel;
};

export class AutoAnomaliesApi {
  constructor(private readonly agent: AxiosInstance) {}

  fetchList(
    { wellId, page, size, sort, filter }: TAutoAnomaliesRequestParams,
    signal?: AbortSignal,
  ): Promise<TPageResponse<TAutoAnomalyRowDto>> {
    return this.agent
      .get<TPageResponse<TAutoAnomalyRowDto>>('/anomaly-registry/api/v1/anomalies/auto/paged', {
        params: {
          wellId,
          page,
          size,
          sort: sortModelToJsonParam(sort ?? []),
          filter: filterModelToJsonParam(filter ?? { items: [] }),
        },
        signal,
      })
      .then((r) => r.data);
  }

  deleteRows(ids: string[], signal?: AbortSignal): Promise<void> {
    return this.agent
      .delete<void>('/anomaly-registry/api/v1/anomalies/auto', {
        data: { ids },
        signal,
      })
      .then((r) => r.data);
  }
}

export type TSortItemParam = { field: string; sort: 'asc' | 'desc' };

const DEFAULT_SORT: TSortItemParam[] = [{ field: 'createdAt', sort: 'desc' }];

export const sortModelToJsonParam = (sortModel: GridSortModel): string => {
  if (!sortModel.length) {
    return JSON.stringify(DEFAULT_SORT);
  }
  const items: TSortItemParam[] = sortModel.map((item) => ({
    field: getApiField(item.field as TAutoAnomaliesFilterKeys),
    sort: (item.sort ?? 'asc') as 'asc' | 'desc',
  }));
  return JSON.stringify(items);
};

export const filterModelToJsonParam = (filterModel: GridFilterModel): string => {
  const items =
    filterModel.items?.filter(isApplicableFilterItem).map((item) => ({
      field: getApiField(item.field as TAutoAnomaliesFilterKeys),
      operator: item.operator ?? 'contains',
      value: item.value,
    })) ?? [];
  return JSON.stringify(items);
};
